/**
 * render.js — the widget itself.
 *
 * Strictly additive: exactly one element is appended to the host page and every
 * style lives behind a closed shadow root, so host CSS cannot reach in and this
 * CSS cannot reach out. Nothing existing on the page is read or modified.
 */

import { CSS } from './styles.js';
import { ROOT_ID } from './config.js';

// Insets, not flush to the edge — portals commonly have a fixed footer or chat
// bar in a corner, and a flush banner collides with it.
const POSITIONS = {
  'bottom-right': { bottom: '16px', right: '16px' },
  'bottom-left': { bottom: '16px', left: '16px' },
  'top-right': { top: '16px', right: '16px' },
  'top-left': { top: '16px', left: '16px' },
};

let sheet; // constructed once, adopted by every mount

function styleSheet() {
  if (sheet !== undefined) return sheet;
  try {
    const s = new CSSStyleSheet();
    s.replaceSync(CSS);
    sheet = s;
  } catch (e) {
    sheet = null; // fall back to a <style> node inside the shadow root
  }
  return sheet;
}

const PETAL_COUNT = 34;      // thinned on narrow screens, see petals()
const PETAL_COUNT_SM = 16;
const PETAL_MIN_MS = 5000;   // a full-viewport drop needs longer than a card-sized one
const PETAL_MAX_MS = 9000;
const PETAL_STAGGER_MS = 2600;

/**
 * Petal colours come from the event's own theme, so every holiday gets on-theme
 * petals without touching this file. Pure white is dropped — it is invisible on
 * the light portals this runs on.
 */
function petalColors(ev) {
  const raw = (ev.gradient && ev.gradient.length ? ev.gradient : [ev.titleColor]) || [];
  const out = [];
  for (let i = 0; i < raw.length; i++) {
    const c = typeof raw[i] === 'string' ? raw[i].trim() : '';
    if (c && !/^#?(fff|ffffff)$/i.test(c.replace('#', '#'))) out.push(c);
  }
  return out.length ? out : ['#ff9933'];
}

/**
 * One-shot petal fall across the viewport, played on mount and then torn out
 * of the DOM.
 *
 * The field is pointer-transparent decoration only — it never intercepts a
 * click and nothing on the host page is read or restyled. It is inserted
 * before the card so the banner itself stays crisp on top.
 *
 * Returns a cleanup function; never throws.
 */
function petals(ev, root, before) {
  try {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || ev.petals === false) return null;

    const colors = petalColors(ev);
    const field = document.createElement('div');
    field.className = 'petals';
    field.setAttribute('aria-hidden', 'true');

    // Percentages inside translate() resolve against the moving element, not
    // its container, so the drop distance has to be a pixel value.
    const vh = window.innerHeight || 800;
    const vw = window.innerWidth || 1024;
    const fall = vh + 80;
    const count = vw < 560 ? PETAL_COUNT_SM : PETAL_COUNT;

    let longest = 0;
    for (let i = 0; i < count; i++) {
      const p = document.createElement('i');
      p.className = 'petal';
      const dur = PETAL_MIN_MS + Math.random() * (PETAL_MAX_MS - PETAL_MIN_MS);
      const delay = Math.random() * PETAL_STAGGER_MS;
      longest = Math.max(longest, dur + delay);

      p.style.setProperty('--c', colors[i % colors.length]);
      p.style.setProperty('--s', (7 + Math.random() * 8).toFixed(1) + 'px');
      p.style.setProperty('--d', Math.round(dur) + 'ms');
      p.style.setProperty('--dl', Math.round(delay) + 'ms');
      p.style.setProperty('--x', (Math.random() * 220 - 110).toFixed(1) + 'px');
      p.style.setProperty('--r', Math.round(180 + Math.random() * 540) + 'deg');
      p.style.setProperty('--fall', fall + 'px');
      p.style.left = (Math.random() * 100).toFixed(1) + '%';
      field.appendChild(p);
    }

    root.insertBefore(field, before || null);

    // Play once: drop the whole field as soon as the last petal has landed, so
    // no animation or node outlives the effect.
    const timer = setTimeout(function () {
      if (field.parentNode) field.parentNode.removeChild(field);
    }, longest + 250);

    return function () {
      clearTimeout(timer);
      if (field.parentNode) field.parentNode.removeChild(field);
    };
  } catch (e) {
    return null; // decoration is never worth breaking the banner over
  }
}

/** Only http(s) links survive — the calendar is remote JSON and treated as untrusted. */
function safeUrl(url) {
  try {
    const parsed = new URL(url, location.href);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch (e) {
    return null;
  }
}

/**
 * Mount the banner.
 *
 * @param {object}   ev       normalized event from schedule.js
 * @param {Function} onClose  called when the user dismisses it
 * @returns {{destroy: Function}}
 */
export function mount(ev, onClose) {
  const host = document.createElement('div');
  host.id = ROOT_ID;

  const base = POSITIONS[ev.position] || POSITIONS['bottom-right'];
  // theme.offset lets a portal with a tall fixed footer push the banner clear of it.
  const place = {};
  for (const edge in base) place[edge] = ev.offset === null ? base[edge] : ev.offset + 'px';
  Object.assign(host.style, {
    position: 'fixed',
    zIndex: '2147483000',
    margin: '0',
    padding: '0',
    border: '0',
    width: 'auto',
    height: 'auto',
    // The host box never swallows clicks meant for the page beneath it;
    // only the card and its close button opt back in.
    pointerEvents: 'none',
  }, place);

  if (ev.width) host.style.setProperty('--tc-width', ev.width + 'px');
  if (ev.titleColor) host.style.setProperty('--tc-title', ev.titleColor);
  if (ev.gradient) {
    host.style.setProperty('--tc-gradient', 'linear-gradient(to right,' + ev.gradient.join(',') + ')');
  }

  const shadow = host.attachShadow({ mode: 'closed' });
  const adopted = styleSheet();
  if (adopted) {
    shadow.adoptedStyleSheets = [adopted];
  } else {
    const style = document.createElement('style');
    style.textContent = CSS;
    shadow.appendChild(style);
  }

  const card = document.createElement('div');
  card.className = 'card';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', ev.title || 'Announcement');

  const close = document.createElement('button');
  close.className = 'close';
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '×';
  card.appendChild(close);

  let media = card;
  const link = ev.link && safeUrl(ev.link);
  if (link) {
    const a = document.createElement('a');
    a.className = 'media';
    a.href = link;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    card.appendChild(a);
    media = a;
  }

  let img = null;
  if (ev.img) {
    img = document.createElement('img');
    img.className = 'img';
    img.src = ev.img;
    img.alt = ev.alt || '';
    img.decoding = 'async';
    img.loading = 'eager';
    media.appendChild(img);
  }

  if (ev.title) {
    const title = document.createElement('h6');
    title.className = 'title' + (ev.gradient ? ' grad' : '');
    // textContent, never innerHTML — the calendar is remote data.
    title.textContent = ev.title;
    media.appendChild(title);
  }

  shadow.appendChild(card);

  let stopPetals = null;
  let alive = true;
  function destroy() {
    if (!alive) return;
    alive = false;
    if (stopPetals) stopPetals();
    document.removeEventListener('keydown', onKey, true);
    if (host.parentNode) host.parentNode.removeChild(host);
  }

  function dismiss() {
    destroy();
    if (typeof onClose === 'function') onClose();
  }

  function onKey(e) {
    if (e.key === 'Escape' || e.keyCode === 27) dismiss();
  }

  close.addEventListener('click', dismiss);
  document.addEventListener('keydown', onKey, true);

  // A missing asset removes the widget rather than showing a broken-image icon.
  if (img) img.addEventListener('error', destroy);

  (document.body || document.documentElement).appendChild(host);

  // After mounting, so the viewport is measurable. Inserted before the card so
  // the petals fall behind the banner rather than across its title.
  stopPetals = petals(ev, shadow, card);

  return { destroy: destroy };
}
